import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Grid } from '@mui/material';
import { ONE_PAGE_SHORT_SECTIONS } from 'constants/businessToolSections/onePageShort';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlan, getSetBusinessToolTitle } from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';

import { CurrencyAutocomplete } from 'components/_basic/CurrencyAutocomplete';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { PhoneInputControlled } from 'components/_basic/Form/PhoneInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { TooltipFormImageUpload } from 'components/BusinessToolForm/components/TooltipFormImageUpload';
import { useHandleRequiredForAIInputBlur } from 'components/BusinessToolForm/hooks/useHandleRequiredForAIInputBlur';
import {
  ABOUT_COMPANY_GENERAL_NAMES,
  prefix as aboutCompanyPrefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/GeneralInfo/utils/form';
import {
  ABOUT_COMPANY_MISSION_NAMES,
  prefix as missionAndVisionPrefix,
} from 'components/BusinessToolForm/Sections/AboutCompany/MissionAndVision/utils/form';
import {
  EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES,
  prefix as executiveSummaryPrefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialInformationExecSummary/utils/form';
import { FundingDirectionsContainer } from 'components/BusinessToolForm/Sections/ExecutiveSummary/FinancialNeedsExecSummary/components/FundingDirectionsContainer';
import {
  EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES,
  prefix as executiveSummaryTeamPrefix,
} from 'components/BusinessToolForm/Sections/ExecutiveSummary/TeamExecSummary/utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from 'components/BusinessToolForm/Sections/Global/form';
import {
  COMPANY_OVERVIEW_NAMES,
  prefix as companyOverviewPrefix,
} from 'components/BusinessToolForm/Sections/Overview/CompanyOverview/utils/form';
import { useHeaderLogic } from 'components/Header/HeaderLogicProvider';

import { getValidatedMetadata } from 'helpers/metadataParser';
import { concatWithDot } from 'helpers/string';

export const OnePageShort: FC = () => {
  const { t } = useTranslation();

  const { control, getValues } = useFormContext();
  const { isPayedSubscription } = useHeaderLogic();
  const { isAuthenticated } = useAuthenticated();

  const [companyNamePrevState, setCompanyNamePrevState] = useState(
    getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)),
  );

  const { id: businessPlanId = '' } = useParams();

  const authUser = useUserStore(getAuthUser);

  const setBusinessToolTitle = useBusinessToolStore(getSetBusinessToolTitle);

  const businessPlan = useBusinessToolStore(getBusinessPlan);

  const updateMutation = useUpdateBusinessPlan();
  const canAccess = !isAuthenticated || isPayedSubscription;
  const handleRequiredForAIInputBlur = useHandleRequiredForAIInputBlur();

  const handleCompanyNameBlur = async () => {
    handleRequiredForAIInputBlur();
    const companyName = getValues(concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME));
    const sameNames = companyNamePrevState === companyName;

    if (authUser && !sameNames) {
      try {
        const params = {
          id: businessPlanId,
          body: {
            title: companyName,
            metadata: JSON.stringify({
              ...getValidatedMetadata(businessPlan.metadata),
              isFirstTryToSetBusinessToolTitle: false,
            }),
          },
        };
        await updateMutation.mutateAsync(params);
        setBusinessToolTitle(companyName);
        setCompanyNamePrevState(companyName);
      } catch {
        return;
      }
    }
  };

  return (
    <>
      <FormSectionCard variant="large">
        <FormHeader title={t('onePageHeader')} badgeText={t('short')} section={ONE_PAGE_SHORT_SECTIONS.SHORT} />
      </FormSectionCard>
      <FormSectionCard title={t('addCompanyLogoAndName')}>
        <Grid container spacing={3}>
          <Grid item xs="auto">
            <TooltipFormImageUpload
              width={64}
              height={64}
              placeholder="Logo"
              name={concatWithDot(companyOverviewPrefix, COMPANY_OVERVIEW_NAMES.FILE)}
              disabled={!canAccess}
            />
          </Grid>
          {/* 0.1.1 */}
          <Grid item xs>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.name"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_NAME)}
              label={t('overview.company.name')}
              maxLength={70}
              onBlur={handleCompanyNameBlur}
              requiredForAI
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard
        title={t('overview.company.briefCompanyDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
        onBlur={handleRequiredForAIInputBlur}
      >
        <TextInputControl
          multiline
          control={control}
          disabled={!canAccess}
          id="onePage.aboutTheCompany.briefCompanyDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.COMPANY_BRIEF_DESCRIPTION)}
          minRows={4}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.websiteUrl"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.WEBSITE_URL)}
              label={t('company.general.websiteUrl')}
              maxLength={150}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <PhoneInputControlled
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.phone"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE)}
              subName={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.PHONE_FORMAT)}
              label={t('company.general.phone')}
            />
          </Grid>
          <Grid item lg={6} xs={12} md={12}>
            <TextInputControl
              control={control}
              disabled={!canAccess}
              id="onePage.aboutTheCompany.email"
              name={concatWithDot(aboutCompanyPrefix, ABOUT_COMPANY_GENERAL_NAMES.EMAIL)}
              label={t('company.general.email')}
              maxLength={150}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard
        title={t('overview.project.briefProjectDescription')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
        aiOnlyUpdate
        disabled={!canAccess}
        requiredForAI
        onBlur={handleRequiredForAIInputBlur}
      >
        {/* 0.2.3 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.briefProjectDescription"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_BRIEF_DESCRIPTION)}
          disabled={!canAccess}
          minRows={4}
          maxLength={1000}
          requiredForAI
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.problemOfMarket')}
        aiFieldName={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
      >
        {/* 0.2.3 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.aboutTheCompany.problemOfMarket"
          name={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.PROBLEM_OF_MARKET)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
          requiredForAI
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('company.mission.howProjectAddressesTheProblem')}
        aiFieldName={concatWithDot(
          missionAndVisionPrefix,
          ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM,
        )}
      >
        <TextInputControl
          multiline
          control={control}
          id="company.mission.howProjectAddressesTheProblem"
          name={concatWithDot(missionAndVisionPrefix, ABOUT_COMPANY_MISSION_NAMES.HOW_PROJECT_ADDRESSES_THE_PROBLEM)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
          requiredForAI
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.market.targetMarket')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TARGET_MARKET)}
      >
        {/* 1.4.1 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.market.targetMarket"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TARGET_MARKET)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.market.mainCompetitors')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_COMPETITORS)}
      >
        {/* 1.4.5 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.market.mainCompetitors"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MAIN_COMPETITORS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('revenueStreamsAndKeyPartners')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REVENUE_STREAMS)}
      >
        {/* 1.4.4 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.marketing.revenueStreams"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.REVENUE_STREAMS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('marketingStrategyOutline')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MARKETING_STRATEGY)}
      >
        {/* 1.4.4 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.market.marketingStrategy"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.MARKETING_STRATEGY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('executiveSummary.team.teamMembers')}
        aiFieldName={concatWithDot(executiveSummaryTeamPrefix, EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS)}
        aiOnlyUpdate
      >
        {/* 1.5.1 */}
        <TextInputControl
          multiline
          control={control}
          id="onePage.team.teamMembers"
          name={concatWithDot(executiveSummaryTeamPrefix, EXECUTIVE_SUMMARY_ORGANIZATION_AND_TEAM_NAMES.MEMBERS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1000}
        />
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12}>
            <Grid item xs={12} md={12} lg={6} paddingRight="12px">
              <CurrencyAutocomplete label={t('selectCurrencyForEntireDocument')} disabled={!canAccess} />
            </Grid>
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedAnnualIncome"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECTED_ANNUAL_INCOME)}
              label={t('executiveSummary.financialInformation.projectedAnnualIncome')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedAnnualProfit"
              name={concatWithDot(
                executiveSummaryPrefix,
                EXECUTIVE_SUMMARY_FINANCIAL_INFO_NAMES.PROJECTED_ANNUAL_PROFIT,
                // EXECUTIVE_SUMMARY.financialInformation.projectedAnnualProfit
              )}
              label={t('executiveSummary.financialInformation.projectedAnnualProfit')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.forecastedProfitability"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FORECASTED_PROFITABILITY)}
              label={t('executiveSummary.financialInformation.forecastedProfitability')}
              adornmentType="percentage"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialProjection.projectedPlayback"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PROJECT_PAYBACK_PERIOD)}
              label={t('executiveSummary.financialInformation.projectedPlayback')}
              disabled={!canAccess}
              maxLength={3}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialNeeds.totalCostOfYourProject"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.TOTAL_COST)}
              label={t('executiveSummary.financialNeeds.totalCostOfYourProject')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialNeeds.fundingAmount"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.FUNDING_AMOUNT)}
              label={t('executiveSummary.financialNeeds.fundingAmount')}
              adornmentType="currency"
              disabled={!canAccess}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <NumberInputControlled
              control={control}
              id="onePage.financialNeeds.percentageOfFundingToCover"
              name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PERCENTAGE_OF_FUNDING)}
              label={t('executiveSummary.financialNeeds.percentageOfFundingToCover')}
              adornmentType="percentage"
              disabled={!canAccess}
            />
          </Grid>
        </Grid>
      </FormSectionCard>
      <FormSectionCard title={t('executiveSummary.financialNeeds.fundingDirections')} disabled={!canAccess}>
        <FundingDirectionsContainer disabled={!canAccess} />
      </FormSectionCard>
    </>
  );
};
